import React, {useMemo, useState} from 'react';
import {Modal} from "react-bootstrap";
import Money from "../../../../components/Common/Money";
import {useTranslation, withTranslation} from "react-i18next";
import {ReactComponent as PlusIco} from "../../../../../images/icons/plus.svg";
import {ReactComponent as MinusIco} from "../../../../../images/icons/minus.svg";
import MenuEditItemViewHelper from "../../../../services/Menu/services/ItemView/MenuEditItemViewHelper";
import ProductAvailability from "./ProductAvailability";
import ErrorFormAlertView from "../../../../components/Common/ErrorFormAlertView";
import {scrollToProductRequiredSection} from "../../../../utils/product";
import StoreUtils from "../../../../services/Store/StoreUtils";
import HoursHelper from "../../../../services/Menu/services/Availability/HoursHelper";
import DateService from "../../../../services/Common/Date/DateService";
import ProductChangeOrderDetailsModal from "./ChangeOrderDetailsModal/ProductChangeOrderDetailsModal";
import ItemViewHelper from "../../../../services/Menu/services/Item/ItemViewHelper";
import StateItemHelper from "../../../../services/Menu/services/State/StateItemHelper";
import PromotionHelper from "../../../../services/Menu/services/Promotion/PromotionHelper";
import {RootState} from "../../../../../reducers";
import {
    getAvailabilities,
    getItemGroups,
    getItems,
    getMenus,
    getModifierGroups,
    getPromotions
} from "../../../../services/Menu/reducer";
import {getSession, selectLocale} from "../../../../services/Session/reducer";
import {getStoreConfig} from "../../../../services/Store/reducer";
import {getCart} from "../../../../services/Cart/reducer";
import {connect} from "react-redux";

interface OwnProps {
    productState: Record<string, any>;
    updateProductState: (productState: Record<string, any>) => void;
    simple?: true;
    onSubmit: () => void;
    productView: Record<string, any>;
    product: Record<string, any>;
    setIsProductAvailable: React.Dispatch<React.SetStateAction<boolean>>;
    isProductAvailable: boolean;
    errors: any[];
    loading: boolean;
    productItemGroup: Record<string, any> | undefined;
    setHasNoAvailableInfo: React.Dispatch<React.SetStateAction<boolean>>;
    menuId: number | undefined;
    productRestricted: boolean;
    externalProductState?: Record<string, any>;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const MenuProductModalFooter = ({
                                    productState,
                                    updateProductState,
                                    simple,
                                    onSubmit,
                                    productView,
                                    setIsProductAvailable,
                                    isProductAvailable,
                                    product,
                                    errors,
                                    loading,
                                    productItemGroup,
                                    config,
                                    setHasNoAvailableInfo,
                                    menuId,
                                    productRestricted,
                                    externalProductState,
                                    session,
                                    cart,
                                    items,
                                    availabilities,
                                    modifierGroups,
                                    promotions
                                }: Props) => {
    const {t} = useTranslation();
    const isValid = MenuEditItemViewHelper.isValid(productView);
    const [possibilityToChangeOrderDetails, setPossibilityToChangeOrderDetails] = useState(true);
    const isStoreOpen = HoursHelper.isOpen(config?.hours || [], DateService.emptyOrNow(session.pickup_at));
    const disabled = isStoreOpen ? (productView.disabled || !isProductAvailable || productRestricted) : !possibilityToChangeOrderDetails;
    const [isAvailabilityInfo, setIsAvailabilityInfo] = useState(true);
    const isReadonly = StoreUtils.isOrderingReadonly(config);
    const areQuantityButtonsVisible = useMemo(() => {
        let result = isProductAvailable;

        if (productRestricted && product.energy_drink) result = false;

        return result;
    }, [productRestricted]);
    const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);


    const increaseQuantity = () => {
        const newQuantity = productState.quantity + 1;
        updateProductState({
            ...productState,
            quantity: newQuantity,
        });
    };

    const decreaseQuantity = () => {
        const newQuantity = productState.quantity - 1;
        updateProductState({
            ...productState,
            quantity: newQuantity,
        });
    };

    const handleOnSubmit = () => {
        if (isStoreOpen) {
            if (!isValid) {
                scrollToProductRequiredSection();
                return;
            }

            onSubmit();
        } else if (possibilityToChangeOrderDetails) {
            setShowOrderDetailsModal(true);
        }
    }

    const handleSaveOrderDetails = (session: Record<string, any>) => {
        const parsedItemGroup = productItemGroup ? ItemViewHelper.parseItemGroup(
            productItemGroup, items, availabilities, [],
            session.pickup_at, 0, cart, session.order_type
        ) : undefined;
        const itemId = parsedItemGroup ? parsedItemGroup.items.find((item: Record<string, any>) => item.order_types.length === 0 || item.order_types.includes(session.order_type)).item_id : product.id;
        const newItemState = StateItemHelper.create(itemId, items, modifierGroups, session.order_type);
        StateItemHelper.refreshItemPrice(newItemState, PromotionHelper.getForItem(promotions, productState.item_id), cart);
        updateProductState(newItemState)
    }

    if (simple) {
        return (
            <Modal.Footer>
                <button
                    className={`btn-spinner w-100 btn-primary btn btn-submit btn-submit-fixed-bottom ${loading ? "loading" : ""}`}
                    disabled={disabled} onClick={handleOnSubmit}>
                    <span className="left spinner-border spinner-border-sm"/>
                    {`${t("common.action.add")}: `}
                    <span>
                        <Money
                            value={productState.discount_price ? productState.discount_price : productState.total_price}
                            currency={productState.total_price.currency}/>
                    </span>
                </button>
            </Modal.Footer>
        )
    }

    if (isReadonly && !isAvailabilityInfo) return <></>;

    return (
        <Modal.Footer>
            <ProductAvailability productView={productView} product={product}
                                 productItemGroup={productItemGroup}
                                 setIsAvailabilityInfo={setIsAvailabilityInfo}
                                 setIsProductAvailable={setIsProductAvailable}
                                 setHasNoAvailableInfo={setHasNoAvailableInfo}
                                 menuId={menuId}
                                 setShowOrderDetailsModal={setShowOrderDetailsModal}
                                 setPossibilityToChangeOrderDetails={setPossibilityToChangeOrderDetails}
            />
            {
                !isReadonly && (
                    <>
                        <ErrorFormAlertView errors={errors}/>
                        <div className="btn-group-submit">
                            {areQuantityButtonsVisible && (
                                <div className="input-group input-group-quantity gap-0">
                                    <div className="input-group-prepend">
                                        <button type="button" className="btn btn-default btn-icon"
                                                onClick={decreaseQuantity} disabled={productState.quantity <= 1}
                                                aria-label={`${t("common.action.decrease_quantity")} ${productState.name}`}>
                                            <MinusIco aria-hidden="true"/>
                                        </button>
                                    </div>
                                    <span className="input-group-text">{productState.quantity}</span>
                                    <div className="input-group-append">
                                        <button type="button" className="btn btn-default btn-icon"
                                                onClick={increaseQuantity}
                                                aria-label={`${t("common.action.increase_quantity")} ${productState.name}`}>
                                            <PlusIco aria-hidden="true"/>
                                        </button>
                                    </div>
                                </div>
                            )}
                            <button
                                className={`btn-spinner w-100 btn-primary btn btn-submit btn-submit-fixed-bottom ${loading ? "loading" : ""}`}
                                disabled={disabled} onClick={handleOnSubmit}>
                                <span className="left spinner-border spinner-border-sm"/>
                                {isProductAvailable ? `${t("common.action.add")}: ` : t("common.action.add")}
                                {isProductAvailable && (
                                    <span>
                        <Money
                            value={productState.discount_price ? productState.discount_price : productState.total_price}
                            currency={productState.total_price.currency}/>
                    </span>
                                )}
                            </button>
                        </div>
                    </>
                )
            }
            {
                showOrderDetailsModal && (
                    <ProductChangeOrderDetailsModal show={showOrderDetailsModal}
                                                    onHide={() => setShowOrderDetailsModal(false)}
                                                    product={product}
                                                    productItemGroup={productItemGroup}
                                                    menuId={menuId}
                                                    externalProductState={externalProductState}
                                                    onSave={handleSaveOrderDetails}
                    />
                )
            }
        </Modal.Footer>
    )
}

const mapStateToProps = (state: RootState) => ({
    availabilities: getAvailabilities(state.menu),
    session: getSession(state.session),
    locale: selectLocale(state.session),
    config: getStoreConfig(state.config),
    menus: getMenus(state.menu),
    items: getItems(state.menu),
    promotions: getPromotions(state.menu),
    cart: getCart(state.cart),
    itemGroups: getItemGroups(state.menu),
    modifierGroups: getModifierGroups(state.menu)
});

const connector = connect(mapStateToProps);
export default withTranslation()(connector(MenuProductModalFooter));